import {Injectable} from '@angular/core';
import {io} from 'socket.io-client';
import {BehaviorSubject, Observable, Subject} from 'rxjs';
import {environment} from '~local/environments/environment';
import {AuthenticationService} from '~local/app/core/services/authentication.service';
import {filter, map} from 'rxjs/operators';

export enum SocketState {
    DISCONNECTED = 'disconnected',
    CONNECTING = 'connecting',
    CONNECTED = 'connected',
}

export type SocketMessage<T> = {
    isError: boolean,
    payload: T,
};

@Injectable()
export class SocketService {

    protected readonly _state$ = new BehaviorSubject<SocketState>(SocketState.DISCONNECTED);
    protected readonly socket = io(environment.socketHost, {transports: ['websocket'], auth: {token: 'asd'}, autoConnect: false});

    protected readonly _messages$ = new Subject<SocketMessage<any>>();

    public constructor(
        protected readonly authenticationService: AuthenticationService,
    ) {
        this.onSocketConnect = this.onSocketConnect.bind(this);
        this.onSocketDisconnect = this.onSocketDisconnect.bind(this);
        this.onSocketError = this.onSocketError.bind(this);

        // this.socket.on('reconnect', this.onConnect.bind(this));
        this.socket.on('connect', this.onSocketConnect);
        this.socket.on('disconnect', this.onSocketDisconnect);
        this.socket.on('error', this.onSocketError);

        this.authenticationService.credentials$.subscribe($credentials => {
            if ($credentials) {
                this.connectSocket($credentials.token);
            } else {
                this.disconnectSocket();
            }
        });
    }

    protected connectSocket($token: string): void {
        this.socket.auth = {token: $token};

        if (this.socket.connected || this._state$.value === SocketState.CONNECTING) {
            // FIXME: Check new token over exising connection and handle unauthorized errors over socket too! And disconnect only then!
            // this.socket.disconnect();
        }

        if (!this.socket.connected && this._state$.value === SocketState.DISCONNECTED) {
            this._state$.next(SocketState.CONNECTING);
            this.socket.connect();
        }
    }

    protected disconnectSocket(): void {
        this.socket.disconnect();
    }

    private onSocketError($error: any) {
        console.error('onSocketError', $error);
        this._messages$.next({
            isError: true,
            payload: $error,
        });
    }

    private onSocketConnect() {
        // This is called twice, so extra check is added
        if (this._state$.value !== SocketState.CONNECTED) {
            this._state$.next(SocketState.CONNECTED);
        }
    }

    private onSocketDisconnect() {
        if (this._state$.value !== SocketState.DISCONNECTED) {
            this._state$.next(SocketState.DISCONNECTED);
        }
    }

    public get state$(): Observable<SocketState> {
        return this._state$;
    }

    public get messages$(): Observable<any> {
        return this._messages$.pipe(
            filter($ => !$.isError),
            map($ => $.payload),
        );
    }

    public get errors$(): Observable<any> {
        return this._messages$.pipe(
            filter($ => $.isError),
            map($ => $.payload),
        );
    }
}
